<template>
  <div class="metadata-wrapper">
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading cycle details...
    </div>
    
    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-triangle"></i> {{ error }}
    </div>

    <div v-else class="metadata-grid">
      <!-- IPROBE_ANALYZE -->
      <div class="metadata-card">
        <div class="card-header">
          <i class="fas fa-microscope"></i>
          <h4>Analysis Details</h4>
        </div>
        <div class="card-body" v-if="metadata.analyze">
          <div class="data-row"><span class="key">Synced Date:</span> <span class="value">{{ formatDate(metadata.analyze.SYNCED_DATE) }}</span></div>
          <div class="data-row"><span class="key">Synced Try:</span> <span class="value">{{ metadata.analyze.SYNCED_TRY }}</span></div>
          <div class="data-row"><span class="key">Cycle Length:</span> <span class="value">{{ formatNumber(metadata.analyze.CYCLE_LENGTH) }}</span></div>
          <div class="data-row"><span class="key">Result:</span> <span class="value result-badge" :class="getResultClass(metadata.analyze.RESULT)">{{ metadata.analyze.RESULT }}</span></div>
          <div class="data-row"><span class="key">Created At:</span> <span class="value">{{ formatDate(metadata.analyze.CREATED_AT) }}</span></div>
          <!-- Channels -->
          <div class="data-row" style="margin-top: 8px; border-bottom: none;"><span class="key" style="color: #007bff;"><i class="fas fa-thermometer-half"></i> Temperatures:</span></div>
          <div class="data-row"><span class="key">CH1 (Temp 1):</span> <span class="value">{{ formatNumber(metadata.analyze.CH1) }} &deg;C</span></div>
          <div class="data-row"><span class="key">CH2 (Temp 2):</span> <span class="value">{{ formatNumber(metadata.analyze.CH2) }} &deg;C</span></div>
          <div class="data-row"><span class="key">CH3 (Temp 3):</span> <span class="value">{{ formatNumber(metadata.analyze.CH3) }} &deg;C</span></div>
          <div class="data-row"><span class="key">CH4 (Temp 4):</span> <span class="value">{{ formatNumber(metadata.analyze.CH4) }} &deg;C</span></div>
          
          <div class="data-row" style="margin-top: 8px; border-bottom: none;"><span class="key" style="color: #28a745;"><i class="fas fa-tachometer-alt"></i> Pressures:</span></div>
          <div class="data-row"><span class="key">CH5 (Pressure 1):</span> <span class="value">{{ formatNumber(metadata.analyze.CH5) }} bar</span></div>
          <div class="data-row"><span class="key">CH6 (Pressure 2):</span> <span class="value">{{ formatNumber(metadata.analyze.CH6) }} bar</span></div>
          <div class="data-row"><span class="key">CH7 (Pressure 3):</span> <span class="value">{{ formatNumber(metadata.analyze.CH7) }} bar</span></div>
          <div class="data-row"><span class="key">CH8 (Pressure 4):</span> <span class="value">{{ formatNumber(metadata.analyze.CH8) }} bar</span></div>
        </div>
        <div class="card-body empty-state" v-else>
          No record found for this category.
        </div>
      </div>

      <!-- CYCLE_FAILURES -->
      <div class="metadata-card">
        <div class="card-header">
          <i class="fas fa-times-circle"></i>
          <h4>Cycle Failures</h4>
        </div>
        <div class="card-body" v-if="metadata.failures">
          <div class="data-row"><span class="key">Fault Name:</span> <span class="value text-danger">{{ metadata.failures.FAULT_NAME }}</span></div>
          <div class="data-row"><span class="key">Category:</span> <span class="value">{{ metadata.failures.CATEGORY }}</span></div>
          <div class="data-row"><span class="key">Locations:</span> <span class="value">{{ metadata.failures.LOCATIONS }}</span></div>
          <div class="data-row"><span class="key">Comment:</span> <span class="value">{{ metadata.failures.COMMENT || '-' }}</span></div>
          <div class="data-row"><span class="key">Created At:</span> <span class="value">{{ formatDate(metadata.failures.CREATED_AT) }}</span></div>
        </div>
        <div class="card-body empty-state" v-else>
          No record found for this category.
        </div>
      </div>

      <!-- CYCLE_PROCESS_LOGS -->
      <div class="metadata-card">
        <div class="card-header">
          <i class="fas fa-clipboard-list"></i>
          <h4>Process Logs</h4>
        </div>
        <div class="card-body" v-if="metadata.logs">
          <div class="data-row"><span class="key">Is Faulty:</span> <span class="value">{{ metadata.logs.IS_FAULTY ? 'Yes' : 'No' }}</span></div>
          <div class="data-row"><span class="key">Fault Name:</span> <span class="value">{{ metadata.logs.FAULT_NAME || '-' }}</span></div>
          <div class="data-row"><span class="key">Alarm Status:</span> <span class="value">{{ metadata.logs.ALARM_STATUS || '-' }}</span></div>
          <div class="data-row"><span class="key">Responded By:</span> <span class="value">{{ metadata.logs.RESPONDED_BY || '-' }}</span></div>
          <div class="data-row"><span class="key">Response Comment:</span> <span class="value">{{ metadata.logs.RESPONSE_COMMENT || '-' }}</span></div>
          <div class="data-row"><span class="key">Responded At:</span> <span class="value">{{ formatDate(metadata.logs.RESPONDED_AT) }}</span></div>
          <div class="data-row" style="flex-direction: column; align-items: flex-start; border-bottom: none; margin-top: 5px;">
            <span class="key" style="margin-bottom: 5px;">Analysis Details:</span>
            <span class="value" style="text-align: left; font-weight: 500; font-size: 0.9em; white-space: pre-wrap; background: #f8f9fa; padding: 10px; border-radius: 8px; width: 100%; border: 1px solid #e9ecef; box-sizing: border-box; color: #444;">{{ metadata.logs.ANALYSIS_DETAILS || '-' }}</span>
          </div>
        </div>
        <div class="card-body empty-state" v-else>
          No record found for this category.
        </div>
      </div>

      <!-- ENVIRONMENT_PARAMS -->
      <div class="metadata-card">
        <div class="card-header">
          <i class="fas fa-temperature-high"></i>
          <h4>Environment</h4>
        </div>
        <div class="card-body" v-if="metadata.env">
          <div class="data-row"><span class="key">Temperature:</span> <span class="value">{{ formatNumber(metadata.env.TEMPERATURE) }} &deg;C</span></div>
          <div class="data-row"><span class="key">Humidity:</span> <span class="value">{{ formatNumber(metadata.env.HUMIDITY) }}%</span></div>
          <div class="data-row" v-if="metadata.env.THERMAL_IMAGE_PATH">
            <span class="key">Thermal Image:</span> 
            <span class="value"><a :href="metadata.env.THERMAL_IMAGE_PATH" target="_blank" class="link-btn">View Image</a></span>
          </div>
          <div class="data-row"><span class="key">Created At:</span> <span class="value">{{ formatDate(metadata.env.CREATE_AT) }}</span></div>
        </div>
        <div class="card-body empty-state" v-else>
          No record found for this category.
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { callProtectedApi } from '../api';

export default {
  name: 'CycleMetadata',
  props: {
    stationId: {
      type: [String, Number],
      required: true
    },
    cycleId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      metadata: {
        analyze: null,
        failures: null,
        logs: null,
        env: null
      }
    };
  },
  watch: {
    cycleId: {
      immediate: true,
      handler(newCycleId) {
        if (newCycleId) {
          this.fetchMetadata();
        }
      }
    }
  },
  methods: {
    async fetchMetadata() {
      this.loading = true;
      this.error = null;
      try {
        const response = await callProtectedApi(`/cycle-metadata/${this.stationId}/${this.cycleId}`, 'get');
        this.metadata = response.data;
      } catch (err) {
        console.error('Error fetching cycle metadata:', err);
        this.error = 'Failed to load cycle details. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    formatNumber(val) {
      if (val === null || val === undefined) return '-';
      const num = parseFloat(val);
      if (isNaN(num)) return val;
      return num.toFixed(2);
    },
    getResultClass(result) {
      if (!result) return '';
      const lower = result.toLowerCase();
      if (lower.includes('pass') || lower === 'ok') return 'badge-success';
      if (lower.includes('fail') || lower === 'ng') return 'badge-danger';
      return 'badge-warning';
    }
  }
};
</script>

<style scoped>
.metadata-wrapper {
  margin-top: 20px;
  padding: 10px;
  animation: fadeIn 0.4s ease-in-out;
}

.loading-state, .error-state {
  text-align: center;
  padding: 30px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  font-size: 1.1em;
  color: #555;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.error-state {
  color: #d9534f;
}

.metadata-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.metadata-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #f0f0f0;
}

.metadata-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e9ecef;
}

.card-header i {
  color: #4b6cb7;
  font-size: 1.2em;
}

.card-header h4 {
  margin: 0;
  font-size: 1.1em;
  color: #333;
  font-weight: 600;
}

.card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state {
  color: #888;
  font-style: italic;
  text-align: center;
  padding: 30px 20px;
}

.data-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 0.95em;
  border-bottom: 1px solid #f5f5f5;
  padding-bottom: 8px;
}

.data-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.key {
  color: #666;
  font-weight: 500;
  flex: 1;
}

.value {
  color: #222;
  font-weight: 600;
  flex: 1.5;
  text-align: right;
  word-break: break-word;
}

.text-danger {
  color: #d9534f;
}

.result-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.85em;
  display: inline-block;
}

.badge-success { background: #d4edda; color: #155724; }
.badge-danger { background: #f8d7da; color: #721c24; }
.badge-warning { background: #fff3cd; color: #856404; }

.link-btn {
  color: #007bff;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.link-btn:hover {
  color: #0056b3;
  text-decoration: underline;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
